/**
 * Store Pinia : fiches d'heures — Phase 6 CP-5.
 *
 * - state.byId : Record<id, FicheHeures> (cache liste)
 * - state.detail : { fiche, lignes } | null (cache vue detail courante)
 * - state.paieSettings : TenantPaieSettings | null (charge a la demande)
 * - actions : fetchList / fetchOne / generate / update / submit /
 *             validateChef / validateDirection / reject / exportExcel
 *             + fetchPaieSettings / updatePaieSettings
 *
 * Strategie cache : pas de stockage persistant. Listes + detail rafraichis
 * a chaque entree sur la vue concernee (les fiches changent souvent).
 */

import { defineStore } from 'pinia';
import { fichesHeuresApi, paieSettingsApi, type FichesHeuresListFilters } from '../api/endpoints';
import type {
  FicheHeures,
  FicheHeuresLigne,
  FicheHeuresLigneInput,
  FicheHeuresStatus,
  TenantPaieSettings,
} from '../types/models';

interface FichesHeuresState {
  byId: Record<number, FicheHeures>;
  allIds: number[];
  detail: { fiche: FicheHeures; lignes: FicheHeuresLigne[] } | null;
  paieSettings: TenantPaieSettings | null;
  loading: boolean;
  error: string | null;
}

/**
 * Extrait un message d'erreur lisible depuis une AxiosError ou ApiError.
 * Format : "[code_erreur] message" si on a les 2, sinon le meilleur dispo.
 */
function extractErrorMessage(e: unknown): string {
  if (e && typeof e === 'object') {
    const obj = e as Record<string, unknown>;
    const response = obj.response as Record<string, unknown> | undefined;
    if (response && typeof response === 'object') {
      const data = response.data as Record<string, unknown> | undefined;
      if (data && typeof data === 'object') {
        const code = typeof data.error === 'string' ? data.error : null;
        const msg = typeof data.message === 'string' ? data.message : null;
        if (code && msg) return `[${code}] ${msg}`;
        if (msg) return msg;
        if (code) return code;
      }
      if (typeof response.status === 'number') {
        return `HTTP ${response.status}`;
      }
    }
    if (typeof obj.message === 'string') return obj.message;
  }
  return 'unknown_error';
}

export const useFichesHeuresStore = defineStore('fichesHeures', {
  state: (): FichesHeuresState => ({
    byId: {},
    allIds: [],
    detail: null,
    paieSettings: null,
    loading: false,
    error: null,
  }),

  getters: {
    list: (state): FicheHeures[] => state.allIds.map((id) => state.byId[id]).filter(Boolean),

    /** Fiches anomales du tenant (pour badge alerte). */
    anomaliesCount(): number {
      return this.list.filter((f) => f.has_anomaly === 1).length;
    },

    /** Fiches en attente de validation N1 chef. */
    pendingChefCount(): number {
      return this.list.filter((f) => f.status === 'submitted').length;
    },

    /** Fiches en attente de validation N2 direction. */
    pendingDirectionCount(): number {
      return this.list.filter((f) => f.status === 'validated_chef').length;
    },
  },

  actions: {
    async fetchList(filters: FichesHeuresListFilters = {}): Promise<void> {
      this.loading = true;
      this.error = null;
      try {
        const { fiches } = await fichesHeuresApi.list(filters);
        this.byId = {};
        this.allIds = [];
        for (const f of fiches) {
          this.byId[f.id] = f;
          this.allIds.push(f.id);
        }
      } catch (e) {
        this.error = extractErrorMessage(e);
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async fetchOne(id: number): Promise<{ fiche: FicheHeures; lignes: FicheHeuresLigne[] }> {
      this.loading = true;
      this.error = null;
      try {
        const detail = await fichesHeuresApi.get(id);
        this.detail = detail;
        this.byId[detail.fiche.id] = detail.fiche;
        if (!this.allIds.includes(detail.fiche.id)) this.allIds.push(detail.fiche.id);
        return detail;
      } catch (e) {
        this.error = extractErrorMessage(e);
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async generate(week_start?: string): Promise<{ created: number; updated: number; week_start: string }> {
      const res = await fichesHeuresApi.generate(week_start);
      await this.fetchList({ week_start: res.week_start });
      return res;
    },

    async update(id: number, patch: {
      site_location?: string | null;
      site_manager_name?: string | null;
      notes_internes?: string | null;
      lignes?: FicheHeuresLigneInput[];
    }): Promise<void> {
      await fichesHeuresApi.update(id, patch);
      await this.fetchOne(id);
    },

    async submit(id: number): Promise<void> {
      await fichesHeuresApi.submit(id);
      await this.fetchOne(id);
    },

    async validateChef(id: number): Promise<void> {
      await fichesHeuresApi.validateChef(id);
      await this.fetchOne(id);
    },

    async validateDirection(id: number): Promise<void> {
      await fichesHeuresApi.validateDirection(id);
      await this.fetchOne(id);
    },

    async reject(id: number, rejection_reason: string): Promise<void> {
      await fichesHeuresApi.reject(id, rejection_reason);
      await this.fetchOne(id);
    },

    async requestCorrection(id: number, reason: string): Promise<void> {
      await fichesHeuresApi.requestCorrection(id, reason);
      await this.fetchOne(id);
    },

    async exportExcel(week_start: string, status: FicheHeuresStatus = 'validated_direction', chantier_id?: number): Promise<Blob> {
      return fichesHeuresApi.exportExcel(week_start, status, chantier_id);
    },

    async fetchPaieSettings(): Promise<TenantPaieSettings> {
      const { paie_settings } = await paieSettingsApi.get();
      this.paieSettings = paie_settings;
      return paie_settings;
    },

    async updatePaieSettings(patch: Partial<TenantPaieSettings>): Promise<void> {
      await paieSettingsApi.update(patch);
      await this.fetchPaieSettings();
    },

    reset(): void {
      this.byId = {};
      this.allIds = [];
      this.detail = null;
      this.paieSettings = null;
      this.error = null;
      this.loading = false;
    },
  },
});
